/* eslint-disable react/react-in-jsx-scope */
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/components/ui/use-toast";
import { useFetchData } from "@/hooks/useFetchData";
import { useFetchDataV2 } from "@/hooks/useFetchDataV2";
import usePostData from "@/hooks/usePostData";
import { createProjectFromTypeSelection } from "@/modules/project-custom/createProjectFromType";
import { AlertTriangle, Clock3, Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

type SubmitSidebarProject = {
  id: number;
  program?: {
    id: number;
    name: string;
  } | null;
};

type SubmitSidebarResponse = {
  projects: SubmitSidebarProject[];
  availablePrograms?: Array<{
    id: number;
    name: string;
    dateFin?: string | null;
    projectType?: {
      id: number;
      name: string;
    } | null;
  }>;
};

const normalizeDateOnly = (raw?: string | null): string | null => {
  if (!raw || !raw.trim()) return null;
  const direct = raw.trim().slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(direct)) return direct;
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return null;
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const day = String(parsed.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatDateForDisplay = (raw?: string | null): string | null => {
  const normalized = normalizeDateOnly(raw);
  if (!normalized) return null;
  const [year, month, day] = normalized.split("-");
  if (!year || !month || !day) return normalized;
  return `${day}/${month}/${year}`;
};

const CallForProjectsEntry = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { postData } = usePostData<any>();
  const { fetchData } = useFetchDataV2("");

  const [isCreatingProject, setIsCreatingProject] = useState(false);
  const [hasTriedAutoCreate, setHasTriedAutoCreate] = useState(false);
  const { data, loading } = useFetchData<SubmitSidebarResponse>(
    `${import.meta.env.VITE_API_ENDPOINT}/call-for-project/submit/sidebar`,
  );

  const projects = Array.isArray(data?.projects) ? data.projects : [];
  const availablePrograms = Array.isArray(data?.availablePrograms)
    ? data.availablePrograms
    : [];

  const firstProject = useMemo(
    () => (projects.length > 0 ? projects[0] : null),
    [projects],
  );
  const selectedProgram = useMemo(
    () => (availablePrograms.length > 0 ? availablePrograms[0] : null),
    [availablePrograms],
  );
  const callDeadline = useMemo(
    () => normalizeDateOnly(selectedProgram?.dateFin),
    [selectedProgram?.dateFin],
  );
  const displayedCallDeadline = useMemo(
    () => formatDateForDisplay(selectedProgram?.dateFin),
    [selectedProgram?.dateFin],
  );
  const isCallExpired = useMemo(() => {
    if (!callDeadline) return false;
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(
      now.getDate(),
    ).padStart(2, "0")}`;
    return today > callDeadline;
  }, [callDeadline]);

  useEffect(() => {
    if (!loading && firstProject?.id) {
      const params = new URLSearchParams();
      params.set("step", "1");
      if (firstProject.program?.id) {
        params.set("programId", `${firstProject.program.id}`);
      }
      if (firstProject.program?.name) {
        params.set("programName", firstProject.program.name);
      }
      navigate(`/submit/appels-a-projets/projet/${firstProject.id}?${params.toString()}`, {
        replace: true,
      });
    }
  }, [firstProject, loading, navigate]);

  useEffect(() => {
    if (loading || firstProject?.id || hasTriedAutoCreate) return;
    if (isCallExpired) return;
    if (!selectedProgram?.projectType?.id) return;

    const typeId = Number(selectedProgram.projectType.id);
    if (!Number.isFinite(typeId) || typeId <= 0) return;

    setHasTriedAutoCreate(true);
    void handleCreateFromType({
      typeId,
      typeLabel:
        selectedProgram.projectType.name || `Type #${selectedProgram.projectType.id}`,
    });
  }, [firstProject?.id, hasTriedAutoCreate, isCallExpired, loading, selectedProgram]);

  const handleCreateFromType = async ({
    typeId,
    typeLabel,
  }: {
    typeId: number;
    typeLabel: string;
  }) => {
    const programId = Number(availablePrograms[0]?.id ?? 0);
    const programName = String(availablePrograms[0]?.name ?? "");

    if (!programId) {
      toast({
        className: "text-red-500",
        title: "Aucun programme disponible pour creer le projet.",
      });
      return;
    }

    setIsCreatingProject(true);
    const result = await createProjectFromTypeSelection({
      fetchData,
      postData,
      programId,
      defaultProjectName: typeLabel,
      typeId: Number(typeId),
      allowCreateWithoutNativeFields: true,
    });
    setIsCreatingProject(false);

    if (!result.success || !result.projectId) {
      toast({
        className: "text-red-500",
        title: result.message || "La creation du projet a echoue.",
      });
      return;
    }

    const params = new URLSearchParams();
    params.set("programId", String(programId));
    params.set("step", "1");
    if (programName) {
      params.set("programName", programName);
    }

    window.location.assign(
      `/submit/appels-a-projets/projet/${result.projectId}?${params.toString()}`,
    );
  };

  if (firstProject) {
    return (
      <div className="rounded-2xl border border-[#e1e8dc] bg-white p-6 text-sm text-[#5f6d5c]">
        <div className="inline-flex items-center gap-2">
          <Loader2 className="size-4 animate-spin text-[#486a2f]" />
          Ouverture de votre projet...
        </div>
      </div>
    );
  }

  if (!loading && isCallExpired) {
    return (
      <div className="rounded-2xl border border-red-200 bg-white p-6 shadow-sm">
        <div className="rounded-2xl border border-red-300 bg-red-50 p-5">
          <div className="flex items-start gap-3">
            <div className="rounded-full bg-red-100 p-2">
              <AlertTriangle className="size-5 text-red-600" />
            </div>
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-lg font-semibold text-red-900">
                  Les candidatures sont closes
                </h2>
                {displayedCallDeadline && (
                  <span className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-white px-2.5 py-1 text-xs font-medium text-red-700">
                    <Clock3 className="size-3.5" />
                    Date limite : {displayedCallDeadline}
                  </span>
                )}
              </div>
              <p className="text-sm leading-6 text-red-800">
                La période de dépôt des candidatures pour cet appel à projets est terminée.
                Il n’est désormais plus possible de créer un compte candidat ou de déposer un projet
                via ce lien.
              </p>
              <p className="text-sm leading-6 text-red-800">
                Si vous pensez qu’il s’agit d’une erreur ou si vous avez besoin d’un nouvel accès,
                nous vous invitons à contacter directement le porteur de projet.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (
    loading ||
    !firstProject?.id ||
    isCreatingProject ||
    !selectedProgram?.projectType?.id
  ) {
    return (
      <div className="rounded-2xl border border-[#e1e8dc] bg-white p-6">
        <div className="mb-4 inline-flex items-center gap-2 text-sm text-[#486a2f]">
          <Loader2 className="size-4 animate-spin" />
          Chargement du formulaire...
        </div>
        <div className="space-y-3 opacity-70">
          <Skeleton className="h-7 w-56 rounded-md" />
          <Skeleton className="h-4 w-72 rounded-md" />
          <Skeleton className="h-44 w-full rounded-xl" />
        </div>
      </div>
    );
  }
  return null;
};

export default CallForProjectsEntry;
